import type { ReceiptAnalysis, CostWarning, CostRecord } from './costModel';

export interface ExpenseSource { id: string; image: string; fileName?: string; capturedAt: string }
export interface ExpenseAnalysis {
  kind: 'receipt' | 'odometer' | 'uncertain' | 'unsupported';
  reasonCode: string;
  capturedAt: string;
  receipt: ReceiptAnalysis | null;
  odometer: { mileage: number | null; unit: 'KM' | 'MI' | null; warnings: CostWarning[] } | null;
}
export interface ReceiptCaptureSeed extends ExpenseSource { analysis: ReceiptAnalysis }
export interface TravelCaptureSeed extends ExpenseSource {
  mileage: number | null; unit: 'KM' | 'MI' | null; warnings: CostWarning[];
}
export function manualReceipt(source: ExpenseSource): ReceiptAnalysis {
  return { amount: null, currency: null, occurredAt: null, capturedAt: source.capturedAt,
    items: [], details: [], warnings: [], requiredFields: ['amount', 'currency', 'occurredAt'] };
}
export const isExpensePdf = (source: ExpenseSource) => source.image.startsWith('data:application/pdf;');

/** Loading or failed lookups must never be interpreted as no active journey. */
export function sameTravelTarget(expected: { id: string } | null, actual: { id: string } | null) {
  return (expected?.id ?? null) === (actual?.id ?? null);
}

/** Only an explicit null from a successful lookup means there is no trip. */
export function expenseActiveTravel(response: unknown, invalidMessage: string): CostRecord | null {
  const value = response as { travel?: CostRecord | null } | null;
  if (value?.travel === null) return null;
  if (!value?.travel || typeof value.travel.id !== 'string' || !value.travel.id || !value.travel.travel
    || ![undefined, 1, 2].includes(value.travel.travel.workflowVersion)) throw new Error(invalidMessage);
  return value.travel;
}

/** Cancelling invalidates even transports which cannot abort a provider call. */
export function expenseAnalysisRequest() {
  let generation = 0;
  let controller: AbortController | null = null;
  return {
    cancel() { generation++; controller?.abort(); controller = null; },
    start() {
      controller?.abort(); const current = ++generation; controller = new AbortController();
      return { signal: controller.signal, current: () => current === generation };
    },
  };
}
